/**
 * ExportButton (FE-2, §4.1). Downloads the current filtered view as CSV via
 * `GET /api/transactions/export.csv` using the SAME filter query string as the
 * list (built by `filtersToSearchParams`, minus limit/offset) and the
 * `downloadFile` transport helper. The server streams a de-DE Excel CSV with a
 * `Content-Disposition` filename; `transaktionen.csv` is the fallback.
 */
import { useState } from 'react';
import { Download } from 'lucide-react';
import { Button } from '../../components/ui/Button.tsx';
import { api, ApiRequestError } from '../../lib/api.ts';
import { useToast } from '../../context/ToastContext.tsx';
import { filtersToSearchParams } from '../../lib/hooks/useTransactions.ts';
import type { TransactionFilters } from '../../lib/types.ts';

export interface ExportButtonProps {
  filters: TransactionFilters;
  disabled?: boolean;
}

export function ExportButton({ filters, disabled }: ExportButtonProps) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  async function onExport() {
    setBusy(true);
    try {
      const qs = filtersToSearchParams(filters).toString();
      const path = qs ? `/api/transactions/export.csv?${qs}` : '/api/transactions/export.csv';
      await api.downloadFile(path, 'transaktionen.csv');
    } catch (error) {
      const message =
        error instanceof ApiRequestError ? error.message : 'Export fehlgeschlagen.';
      toast(message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Button variant="secondary" size="sm" onClick={onExport} disabled={disabled || busy}>
      <Download className="h-4 w-4" />
      {busy ? 'Exportiere …' : 'CSV exportieren'}
    </Button>
  );
}
